import { useCallback, useEffect, useState } from "react";
import {
    adminListSubmissions,
    adminUpdateSubmissionStatus,
    adminDeleteSubmission,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Check,
    Clock,
    Ban,
    XCircle,
    Trash2,
    ExternalLink,
    Mail,
    Phone,
    Building,
    Zap,
    Pencil,
} from "lucide-react";
import SubmissionEditDialog from "@/components/admin/SubmissionEditDialog";

const STATUS_STYLE = {
    draft: { background: "#A89489", color: "#FFFFFF" },
    approved: { background: "#54547A", color: "#FFFFFF" },
    pending: { background: "#F5C53D", color: "#1B1B1B" },
    denied: { background: "#E63B26", color: "#FFFFFF" },
    banned: { background: "#1B1B1B", color: "#F5C53D" },
};

const PROMO_BADGE = {
    Featured: "Featured → home (3 cols, 72h)",
    Enhanced: "Enhanced → Most Recent (4×2, 72h)",
    Standard: "Standard → Latest Posts",
};

function formatWhen(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function StatusPill({ status }) {
    const style = STATUS_STYLE[status] || STATUS_STYLE.draft;
    return (
        <span
            className="inline-block px-2 py-0.5 text-[0.62rem] font-poppins font-semibold tracking-[0.12em] uppercase"
            style={style}
            data-testid={`submission-status-${status}`}
        >
            {status}
        </span>
    );
}

function SubmissionRow({ sub, onAction, onDelete, onEdit }) {
    return (
        <div
            className="border border-black/10 bg-white p-4"
            data-testid={`submission-row-${sub.id}`}
        >
            <div className="flex items-start gap-4">
                {sub.image_url ? (
                    <img
                        src={sub.image_url}
                        alt=""
                        className="w-20 h-20 object-cover bg-[#1B1B1B] shrink-0"
                    />
                ) : (
                    <div className="w-20 h-20 bg-[#1B1B1B]/10 shrink-0 flex items-center justify-center font-poppins text-[0.55rem] tracking-[0.18em] uppercase text-[#1B1B1B]/40">
                        No image
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <StatusPill status={sub.status} />
                        {sub.expedite && (
                            <span
                                className="badge-category"
                                style={{
                                    background: "#F5C53D",
                                    color: "#1B1B1B",
                                }}
                            >
                                <Zap className="inline h-3 w-3 mr-1" />
                                Expedite
                            </span>
                        )}
                        <span className="text-[0.65rem] font-poppins tracking-[0.12em] uppercase text-[#1B1B1B]/60">
                            {PROMO_BADGE[sub.which_promotion] ||
                                sub.which_promotion ||
                                "—"}
                        </span>
                        <span className="text-[0.65rem] font-sofia text-[#1B1B1B]/50 ml-auto">
                            Received {formatWhen(sub.created_at)}
                        </span>
                    </div>
                    <h4 className="font-poppins font-semibold text-base mt-2 truncate">
                        {sub.title || "(no title)"}
                    </h4>
                    <p className="font-sofia text-sm text-[#1B1B1B]/75 mt-1 line-clamp-2">
                        {sub.body || sub.blurb || "—"}
                    </p>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-1 font-sofia text-xs text-[#1B1B1B]/70">
                        <span className="inline-flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {sub.email || "—"}
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {sub.phone || "—"}
                        </span>
                        <span className="inline-flex items-center gap-1 truncate">
                            <Building className="h-3 w-3" />{" "}
                            {sub.business || sub.contact_name || "—"}
                        </span>
                    </div>
                    {sub.admin_note && (
                        <p className="mt-2 text-xs font-sofia italic text-[#1B1B1B]/60 border-l-2 border-[#F5C53D] pl-2">
                            Note: {sub.admin_note}
                        </p>
                    )}
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-black/5">
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEdit(sub)}
                    className="border-black/20 hover:bg-[#FDF9EF] rounded-none"
                    data-testid={`submission-edit-${sub.id}`}
                >
                    <Pencil className="h-3 w-3 mr-1" /> Edit fields
                </Button>
                {sub.status !== "approved" && (
                    <Button
                        size="sm"
                        onClick={() => onAction(sub, "approved")}
                        className="bg-[#54547A] hover:bg-[#1B1B1B] text-white rounded-none"
                        data-testid={`submission-approve-${sub.id}`}
                    >
                        <Check className="h-3 w-3 mr-1" /> Approve
                    </Button>
                )}
                {sub.status !== "pending" && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAction(sub, "pending")}
                        className="border-[#F5C53D] text-[#1B1B1B] hover:bg-[#F5C53D] rounded-none"
                        data-testid={`submission-pending-${sub.id}`}
                    >
                        <Clock className="h-3 w-3 mr-1" /> Pending
                    </Button>
                )}
                {sub.status !== "denied" && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAction(sub, "denied")}
                        className="border-[#E63B26] text-[#E63B26] hover:bg-[#E63B26] hover:text-white rounded-none"
                        data-testid={`submission-deny-${sub.id}`}
                    >
                        <XCircle className="h-3 w-3 mr-1" /> Deny
                    </Button>
                )}
                {sub.status !== "banned" && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onAction(sub, "banned")}
                        className="border-[#1B1B1B] text-[#1B1B1B] hover:bg-[#1B1B1B] hover:text-[#F5C53D] rounded-none"
                        data-testid={`submission-ban-${sub.id}`}
                    >
                        <Ban className="h-3 w-3 mr-1" /> Ban (Spam)
                    </Button>
                )}
                {sub.post_id && (
                    <a
                        href={`/post/${(sub.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-auto inline-flex items-center text-xs font-poppins tracking-[0.18em] uppercase text-[#E63B26] hover:underline"
                    >
                        View live post <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                )}
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(sub)}
                    className="ml-auto text-[#1B1B1B]/40 hover:text-[#E63B26]"
                    data-testid={`submission-delete-${sub.id}`}
                    title="Remove from queue"
                >
                    <Trash2 className="h-3 w-3" />
                </Button>
            </div>
        </div>
    );
}

/**
 * `mode` controls which submissions show:
 *   - "queue"    → everything except banned
 *   - "blacklist" → banned only
 */
export default function SubmissionsPanel({ mode = "queue" }) {
    const [submissions, setSubmissions] = useState([]);
    const [filter, setFilter] = useState("all");
    const [actionDialog, setActionDialog] = useState(null); // { sub, status }
    const [noteDraft, setNoteDraft] = useState("");
    const [editingSub, setEditingSub] = useState(null);

    const reload = useCallback(async () => {
        try {
            const all = await adminListSubmissions();
            const list =
                mode === "blacklist"
                    ? all.filter((s) => s.status === "banned")
                    : all.filter((s) => s.status !== "banned");
            setSubmissions(list);
        } catch (err) {
            console.error("submissions reload failed:", err);
            setSubmissions([]);
        }
    }, [mode]);

    useEffect(() => {
        reload();
    }, [reload]);

    const visible =
        filter === "all"
            ? submissions
            : submissions.filter((s) => s.status === filter);

    const counts = submissions.reduce((acc, s) => {
        acc[s.status] = (acc[s.status] || 0) + 1;
        return acc;
    }, {});

    const startAction = (sub, status) => {
        // Pending always asks for a note. Banned/Deny/Approve can optionally annotate.
        setActionDialog({ sub, status });
        setNoteDraft(sub.admin_note || "");
    };

    const confirmAction = async () => {
        if (!actionDialog) return;
        const { sub, status } = actionDialog;
        try {
            await adminUpdateSubmissionStatus(sub.id, status, noteDraft);
            const messages = {
                approved: "Approved — post published.",
                pending: "Marked pending. Email queued.",
                denied: "Denied. Notification email queued.",
                banned: "Banned. Moved to Blacklist; notification email queued.",
            };
            toast.success(messages[status] || "Updated.");
            setActionDialog(null);
            setNoteDraft("");
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        }
    };

    const removeSubmission = async (sub) => {
        if (!confirm("Remove this submission from the queue permanently?")) return;
        try {
            await adminDeleteSubmission(sub.id);
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    const FILTERS =
        mode === "blacklist"
            ? [{ key: "all", label: "All banned" }]
            : [
                  { key: "all", label: "All" },
                  { key: "draft", label: "Draft" },
                  { key: "pending", label: "Pending" },
                  { key: "approved", label: "Approved" },
                  { key: "denied", label: "Denied" },
              ];

    return (
        <div data-testid={`submissions-panel-${mode}`}>
            {mode !== "blacklist" && (
                <div className="flex flex-wrap gap-2 mb-4">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setFilter(f.key)}
                            className={`px-3 py-1.5 text-xs font-poppins tracking-[0.12em] uppercase border transition ${
                                filter === f.key
                                    ? "bg-[#1B1B1B] text-white border-[#1B1B1B]"
                                    : "bg-white text-[#1B1B1B] border-black/15 hover:border-[#E63B26]"
                            }`}
                            data-testid={`submissions-filter-${f.key}`}
                        >
                            {f.label}
                            {f.key !== "all" && counts[f.key] !== undefined
                                ? ` (${counts[f.key]})`
                                : ""}
                        </button>
                    ))}
                </div>
            )}

            {visible.length === 0 ? (
                <p
                    className="font-sofia text-[#1B1B1B]/60 italic p-6 text-center bg-white border border-dashed border-black/15"
                    data-testid="submissions-empty"
                >
                    {mode === "blacklist"
                        ? "No banned submissions. Spam goes here when you ban it from the Submissions tab."
                        : "No submissions in this view yet. New JotForm submissions land here as drafts."}
                </p>
            ) : (
                <div className="space-y-3">
                    {visible.map((sub) => (
                        <SubmissionRow
                            key={sub.id}
                            sub={sub}
                            onAction={startAction}
                            onDelete={removeSubmission}
                            onEdit={setEditingSub}
                        />
                    ))}
                </div>
            )}

            {editingSub && (
                <SubmissionEditDialog
                    submission={editingSub}
                    onClose={() => setEditingSub(null)}
                    onSaved={reload}
                />
            )}

            <Dialog
                open={!!actionDialog}
                onOpenChange={(o) => {
                    if (!o) setActionDialog(null);
                }}
            >
                <DialogContent
                    className="bg-white sm:max-w-md"
                    data-testid="submissions-action-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-poppins text-2xl text-[#E63B26]">
                            {actionDialog?.status === "approved" && "Approve submission"}
                            {actionDialog?.status === "pending" && "Mark Pending"}
                            {actionDialog?.status === "denied" && "Deny submission"}
                            {actionDialog?.status === "banned" && "Ban submission (Spam)"}
                        </DialogTitle>
                        <DialogDescription className="font-sofia">
                            {actionDialog?.status === "approved" && (
                                <>
                                    A Post will be created automatically and
                                    placed in the
                                    {" "}
                                    <strong>
                                        {actionDialog?.sub?.which_promotion ||
                                            "Standard"}
                                    </strong>
                                    {" "}
                                    bucket. The submitter is not emailed.
                                </>
                            )}
                            {actionDialog?.status === "pending" &&
                                "The submitter will be emailed your note. Replies go to bennebrands@protonmail.com."}
                            {actionDialog?.status === "denied" &&
                                "The submitter will be emailed a standard 'does not comply with our editorial standards' note."}
                            {actionDialog?.status === "banned" &&
                                "The submitter will be emailed a 'violates BBM policies' note and this entry will move to the Blacklist tab."}
                        </DialogDescription>
                    </DialogHeader>

                    {(actionDialog?.status === "pending" ||
                        actionDialog?.status === "denied" ||
                        actionDialog?.status === "banned") && (
                        <div className="mt-2">
                            <Label htmlFor="admin-note">
                                {actionDialog?.status === "pending"
                                    ? "Note to submitter (required)"
                                    : "Internal note (optional)"}
                            </Label>
                            <Textarea
                                id="admin-note"
                                rows={4}
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                placeholder={
                                    actionDialog?.status === "pending"
                                        ? "What's missing or unclear?"
                                        : ""
                                }
                                data-testid="submissions-action-note"
                            />
                        </div>
                    )}

                    <DialogFooter className="mt-4">
                        <Button
                            variant="outline"
                            onClick={() => setActionDialog(null)}
                            className="rounded-none"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmAction}
                            disabled={
                                actionDialog?.status === "pending" &&
                                !noteDraft.trim()
                            }
                            className="bg-[#E63B26] hover:bg-[#1B1B1B] text-white rounded-none"
                            data-testid="submissions-action-confirm"
                        >
                            Confirm{" "}
                            {actionDialog?.status &&
                                actionDialog.status[0].toUpperCase() +
                                    actionDialog.status.slice(1)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
